import React, { useContext } from 'react';
import { z } from 'zod';
import { RestaurantPermissionSchema } from 'src/shared/restaurantTypes';
import { Currency, UuidSchema, PhoneSchema } from 'src/shared/types';

import { createAppRouter } from './router';

import type { Uuid } from 'src/shared/types';
import type { Restaurant } from '@mui/icons-material';

export const apiUrl = process.env.NEXT_PUBLIC_API_URL !== undefined
    ? process.env.NEXT_PUBLIC_API_URL
    : 'https://sandbox.qerko.com';

export const RestaurantCurrencies = [ Currency.CZK, Currency.EUR, Currency.HUF ] as const;

export type RestaurantPermissions = z.infer<typeof RestaurantPermissionSchema>;

export const RestaurantSchema = z.object({
    countryId: z.string(),
    currencies: z.nativeEnum(Currency).array(),
    defaultCurrency: z.nativeEnum(Currency),
    id: UuidSchema,
    identificationNumber: z.string(),
    isActive: z.boolean(),
    isAdyenOnboardingAllowed: z.boolean().default(false),
    isPayuOnboardingAllowed: z.boolean().default(false),
    isPilsnerEnabled: z.boolean(),
    isQerkoOnboardingAllowed: z.boolean().default(false),
    isTesting: z.boolean().nullable(),
    name: z.string(),
    permissions: RestaurantPermissionSchema,
    showPosDriverSettings: z.boolean(),
    showWaiterNameInPayments: z.boolean().nullable(),
    tipReductionForPersonel: z.number(),
    variableSymbol: z.string(),
});

// export type Restaurant = APIv1RestaurantAdmin['GET']['/restaurant']['result']['result'][0];

export interface Restaurant {
    id: Uuid;
    name: string;
    isTesting: boolean | null;
    isActive: boolean;
    showWaiterNameInPayments: boolean | null;
    permissions: RestaurantPermissions;
    tipReductionForPersonel: number;
    variableSymbol: string;
    identificationNumber: string;
    currencies: Currency[];
    defaultCurrency: Currency;
    isPilsnerEnabled: boolean;
    showPosDriverSettings: boolean;
    shiftConfig?: null | Record<string, unknown>;
    countryId: string;
    isAdyenOnboardingAllowed: boolean;
    isPayuOnboardingAllowed: boolean;
    isQerkoOnboardingAllowed: boolean;
}

const SuperadminSchema = z.object({
    ACT_AS_RESTAURANTS: z.boolean().optional(),
    CANCEL_PAYMENT: z.boolean().optional(),
    CONFIRM_PAYMENT: z.boolean().optional(),
    CREATE_RESTAURANTS: z.boolean().optional(),
    LIVE_CONFIG: z.boolean().optional(),
    PAYMENTS: z.boolean().optional(),
    PAYOUT: z.boolean().optional(),
    QRCODES: z.boolean().optional(),
    USERS: z.boolean().optional(),
});
export type SuperAdmin = z.infer<typeof SuperadminSchema>;

export const UserSchema = z.object({
    email: z.string().nullable(),
    emailVerified: z.boolean(),
    id: z.string(),
    isManaged: z.boolean(),
    isPhoneVerified: z.boolean().default(false), // the default value can be removed after a few days
    name: z.string(),
    phone: PhoneSchema.nullable().default(null), // the default value can be removed after a few days
    superadmin: z.union([ z.literal(0), SuperadminSchema ]),
    surname: z.string(),
    username: z.string(),
});

export type User = z.infer<typeof UserSchema>;

export interface Auth {
    accessToken: string;
    restaurants: Restaurant[];
    user: User;
}

export const AuthSchema = z.object({
    accessToken: z.string(),
    restaurants: z.array(RestaurantSchema),
    user: UserSchema,
});

export interface AppContextData {
    auth: Auth | null;
    setAuth: (values: Auth | null, restaurant?: Restaurant | null) => void;
    apiUrl: string;
    restaurant: Restaurant | null;
    setRestaurant: (restaurant: Restaurant | null) => void;
    isSandbox: boolean;
    setSandbox: (val: boolean) => void;
    logout: () => void;
}

export const AppContext = React.createContext<AppContextData>({
    apiUrl,
    auth: null,
    isSandbox: true,
    logout: () => undefined,
    restaurant: null,
    setAuth: () => undefined,
    setRestaurant: () => undefined,
    setSandbox: () => undefined,
});

export const routes = {
    '/': {
        optionalQueries: {},
        queries: {},
        title: 'Home',
    },
    '/adyen-onboarding': {
        optionalQueries: {},
        queries: {},
        title: 'Adyen onboarding',
    },
    '/api-keys': {
        optionalQueries: {},
        queries: {
        },
        title: 'Api keys',
    },
    '/customers': {
        optionalQueries: {
            from: null,
            to: null,
        },
        queries: {},
        title: 'Customers',
    },
    '/dashboard': {
        optionalQueries: {
            from: null,
            to: null,
        },
        queries: {},
        title: 'Dashboard',
    },
    '/eshop': {
        optionalQueries: {},
        queries: {},
        title: 'E-Shop',
    },
    '/eshop/settings': {
        optionalQueries: {},
        queries: {},
        title: 'E-Shop Settings',
    },
    '/feedbacks': {
        optionalQueries: {
            from: null,
            to: null,
        },
        queries: {},
        title: 'Payout',
    },
    '/login': {
        optionalQueries: {
            page: null,
        },
        queries: {},
        title: 'Login',
    },
    '/loyalty': {
        optionalQueries: {},
        queries: {},
        title: 'Loyalty Program',
    },
    '/loyalty/deactivate': {
        optionalQueries: {
            progressing: null,
        },
        queries: {
            id: null,
        },
        title: 'Loyalty Program',
    },
    '/loyalty/offer': {
        optionalQueries: {
            data: null,
            id: null,
        },
        queries: {},
        title: 'Loyalty Program',
    },
    '/menu/daily-menu': {
        optionalQueries: {},
        queries: {
            date: null,
        },
        title: 'Daily menu',
    },
    '/menu/daily-menu/import-config': {
        optionalQueries: {},
        queries: {
        },
        title: 'Daily menu synchronization',
    },
    '/menu/items': {
        optionalQueries: {},
        queries: {},
        title: 'Restaurant Menu Items',
    },
    '/menu/modifiers': {
        optionalQueries: {},
        queries: {},
        title: 'Restaurant Menu Modifiers',
    },
    '/menu/modifiers/edit': {
        optionalQueries: {},
        queries: {
            modifierId: null,
        },
        title: 'Restaurant Menu Modifiers',
    },
    '/menu/modifiers/new': {
        optionalQueries: {},
        queries: {},
        title: 'Restaurant Menu Modifiers',
    },
    '/menu/permanent-menu': {
        optionalQueries: {},
        queries: {
            date: null,
        },
        title: 'Permanent menu',
    },
    '/new-restaurant': {
        optionalQueries: {},
        queries: {},
        title: 'New restaurant',
    },
    '/notifications': {
        optionalQueries: {
        },
        queries: {},
        title: 'Notifications',
    },
    '/orders': {
        optionalQueries: {
            from: null,
            to: null,
        },
        queries: {},
        title: 'Orders',
    },
    '/partner-campaign': {
        optionalQueries: {},
        queries: {},
        title: 'Partner Campaign',
    },
    '/payments': {
        optionalQueries: {
            from: null,
            to: null,
        },
        queries: {},
        title: 'Payout',
    },
    '/payout': {
        optionalQueries: {
            page: null,
        },
        queries: {
            category: null,
        },
        title: 'Payout',
    },
    '/payout-report': {
        optionalQueries: {
            page: null,
        },
        queries: {
            term: null,
        },
        title: 'Payout',
    },
    '/payu-onboarding': {
        optionalQueries: {},
        queries: {},
        title: 'Payu onboarding',
    },
    '/pilsner-campaign': {
        optionalQueries: {},
        queries: {},
        title: 'Pilsner Campaign',
    },
    '/pos-drivers': {
        optionalQueries: {},
        queries: {
        },
        title: 'Pos settings',
    },
    '/profile': {
        optionalQueries: {},
        queries: {},
        title: 'Restaurant profile',
    },
    '/qerko-onboarding': {
        optionalQueries: {},
        queries: {},
        title: 'Qerko onboarding',
    },
    '/qr': {
        optionalQueries: {},
        queries: {
        },
        title: 'Qr codes',
    },
    '/qr-images': {
        optionalQueries: {},
        queries: {
        },
        title: 'Qr images',
    },
    '/registration': {
        optionalQueries: {
        },
        queries: {},
        title: 'Registration',
    },
    '/reservation': {
        optionalQueries: {},
        queries: {
            from: null,
            to: null,
        },
        title: 'Reservation',
    },
    '/reservation/settings': {
        optionalQueries: {},
        queries: {
        },
        title: 'Reservation',
    },
    '/reset-password': {
        optionalQueries: {
        },
        queries: {
            id: null,
        },
        title: 'New password',
    },
    '/settings': {
        optionalQueries: {},
        queries: {
        },
        title: 'Settings',
    },
    '/settings/profile': {
        optionalQueries: {},
        queries: {
        },
        title: 'Settings Profile',
    },
    '/stories': {
        optionalQueries: {},
        queries: {},
        title: 'Stories',
    },
    '/subscriptions': {
        optionalQueries: {},
        queries: {},
        title: 'Subscriptions',
    },
    '/super/live-config': {
        optionalQueries: {},
        queries: {
        },
        title: 'Live config',
    },
    '/super/payment-gateway': {
        optionalQueries: {},
        queries: {
            from: null,
            to: null,
        },
        title: 'Payout',
    },
    '/super/payments': {
        optionalQueries: {},
        queries: {
        },
        title: 'Payments',
    },
    '/super/payout-batch': {
        optionalQueries: {},
        queries: {
        },
        title: 'Payout',
    },
    '/super/payout-batch/edit': {
        optionalQueries: {},
        queries: {
            id: null,
        },
        title: 'QR features',
    },
    '/super/payout-monthly/edit': {
        optionalQueries: {},
        queries: {
            id: null,
        },
        title: 'QR features',
    },
    '/super/payout-report-monthly': {
        optionalQueries: {},
        queries: {
        },
        title: 'Payout report monthly',
    },
    '/super/payout-report-monthly/edit': {
        optionalQueries: {},
        queries: {
            month: null,
        },
        title: 'Payout report monthly',
    },
    '/super/qr-codes': {
        optionalQueries: {},
        queries: {
        },
        title: 'QR codes',
    },
    '/super/qr-features': {
        optionalQueries: {},
        queries: {
        },
        title: 'QR features',
    },
    '/super/restaurant-users': {
        optionalQueries: {},
        queries: {
        },
        title: 'Restaurant users',
    },
    '/super/restaurant-users/edit': {
        optionalQueries: {},
        queries: {
            userId: null,
        },
        title: 'Restaurant users',
    },
    '/super/restaurants': {
        optionalQueries: {},
        queries: {},
        title: 'Super Restaurants',
    },
    '/super/restaurants/debugger': {
        optionalQueries: {},
        queries: {
        },
        title: 'Super Restaurants Debugger',
    },
    '/super/restaurants/detail': {
        optionalQueries: {},
        queries: {
        },
        title: 'Edit restaurant',
    },
    '/super/restaurants/new-restaurant': {
        optionalQueries: {},
        queries: {
        },
        title: 'New restaurant',
    },
    '/super/users': {
        optionalQueries: {},
        queries: {
        },
        title: 'Users',
    },
    '/user/user-info': {
        optionalQueries: {
        },
        queries: {},
        title: 'User edit',
    },
    '/user/user-notifications': {
        optionalQueries: {},
        queries: {
        },
        title: 'Notifications',
    },
    '/user/user-password': {
        optionalQueries: {
        },
        queries: {},
        title: 'User edit',
    },
    '/users': {
        optionalQueries: {},
        queries: {},
        title: 'Users',
    },
} as const;

export const useAppRouter = createAppRouter(routes, {
    persistentQueries: {
        selectedRestaurantId: null,
    },
});

export const AppContextProvider = (props: { value: AppContextData; children: React.ComponentElement<any, any> }) => {
    return (
        <AppContext.Provider value={props.value}>{props.children}</AppContext.Provider>
    );
};

export const useAppContext = (): AppContextData => {
    return useContext(AppContext);
};
